// packages/contract/fixtures/pilates-collective.ts
//
// GATED. A real pilates studio in Kuala Lumpur, still on an unmodified Wix
// template carrying placeholder event copy about San Francisco. This is the
// median customer, not the edge case. First scan, so no deltas anywhere.

import type { LatestSnapshot } from '../contract';

export const pilatesCollective: LatestSnapshot = {
  business: {
    id: 'pilates-collective',
    name: 'The Pilates Collective',
    domain: 'thepilatescollective.my',
    vertical: 'Pilates studio',
    city: 'Kuala Lumpur',
    state: 'prospect',
    plan: null,
  },
  snapshot: {
    id: 'snap-pilates-2026-07',
    businessId: 'pilates-collective',
    capturedAt: '2026-07-08',
    gated: true,
    gateReasons: [
      {
        code: 'placeholder_content',
        plain:
          'Your website still has the sample text it came with. It talks about an event in San Francisco, not your studio.',
        evidence: [
          'Homepage headline reads "Join us at the SF Wellness Expo".',
          'The "About" page is the template\'s Latin placeholder text.',
          'No mention of Kuala Lumpur anywhere on the site.',
        ],
      },
      {
        code: 'no_question_coverage',
        plain:
          'None of your pages answer the questions people actually ask about a pilates studio, so an AI has nothing of yours to repeat.',
        evidence: [
          'No page mentions reformer, prenatal, or beginner classes.',
          'No prices, no class timetable, no instructor names.',
        ],
      },
      {
        code: 'wrong_location',
        plain:
          'Your address is written two different ways across the web, so AI is not sure where you are.',
        evidence: [
          'Website footer: "Bangsar, KL".',
          'Google listing: "Jalan Maarof, Bangsar Baru, 59100".',
          'Facebook: "Kuala Lumpur City Centre".',
        ],
      },
    ],
    total: 16,
    level: 1,
    levelName: 'Getting started',
    plusMinus: 5,
    samplesPerCell: 3,
    previousTotal: null,
    pillars: [
      { key: 'answer_presence', label: 'When people ask AI', kind: 'result', score: 2, max: 35, band: 'not_started' },
      { key: 'search_rank', label: 'When people search Google', kind: 'result', score: 2, max: 20, band: 'not_started' },
      { key: 'site', label: 'Pages AI can read', kind: 'lever', score: 1, max: 15, band: 'not_started' },
      { key: 'sources', label: 'Where AI hears about you', kind: 'lever', score: 2, max: 15, band: 'weak' },
      { key: 'reputation', label: 'What your customers say', kind: 'lever', score: 9, max: 15, band: 'good' },
    ],
    headline: {
      questionsTested: 50,
      mentionsTotal: 4,
      namedRate: 0.08,
      linkRate: 0,
      bestGooglePosition: null,
      sentiment: null,
      sentimentSuppressed: true,
      plainSummary:
        'We paused the deeper AI checks. There is almost nothing on your website for an AI to find and repeat, so the questions people ask about pilates studios turn up other names, not yours.',
    },
    questions: [
      {
        id: 'q-pil-1', text: 'best pilates studio in Kuala Lumpur',
        engines: [
          { engine: 'chatgpt', samples: 3, hits: 0, position: null },
          { engine: 'gemini', samples: 3, hits: 0, position: null },
          { engine: 'claude', samples: 3, hits: 0, position: null },
          { engine: 'perplexity', samples: 3, hits: 1, position: 5 },
          { engine: 'ai_overview', samples: 3, hits: 0, position: null },
        ],
        googleOrganic: null, localPack: 4, siteAnswers: false, actionId: 'a-pil-1',
      },
      {
        id: 'q-pil-2', text: 'reformer pilates near Bangsar',
        engines: [
          { engine: 'chatgpt', samples: 3, hits: 0, position: null },
          { engine: 'gemini', samples: 3, hits: 0, position: null },
          { engine: 'claude', samples: 3, hits: 0, position: null },
          { engine: 'perplexity', samples: 3, hits: 0, position: null },
          { engine: 'ai_overview', samples: 3, hits: 0, position: null },
        ],
        googleOrganic: null, localPack: null, siteAnswers: false, actionId: 'a-pil-2',
      },
      {
        id: 'q-pil-3', text: 'prenatal pilates Kuala Lumpur',
        engines: [
          { engine: 'chatgpt', samples: 3, hits: 0, position: null },
          { engine: 'gemini', samples: 3, hits: 0, position: null },
          { engine: 'claude', samples: 3, hits: 0, position: null },
          { engine: 'perplexity', samples: 3, hits: 0, position: null },
          { engine: 'ai_overview', samples: 3, hits: 0, position: null },
        ],
        googleOrganic: null, localPack: null, siteAnswers: false, actionId: 'a-pil-3',
      },
      {
        id: 'q-pil-4', text: 'beginner pilates classes Bangsar',
        engines: [
          { engine: 'chatgpt', samples: 3, hits: 1, position: 4 },
          { engine: 'gemini', samples: 3, hits: 0, position: null },
          { engine: 'claude', samples: 3, hits: 0, position: null },
          { engine: 'perplexity', samples: 3, hits: 0, position: null },
          { engine: 'ai_overview', samples: 3, hits: 0, position: null },
        ],
        googleOrganic: null, localPack: 6, siteAnswers: false, actionId: 'a-pil-2',
      },
      {
        id: 'q-pil-5', text: 'private pilates instructor KL',
        engines: [
          { engine: 'chatgpt', samples: 3, hits: 0, position: null },
          { engine: 'gemini', samples: 3, hits: 0, position: null },
          { engine: 'claude', samples: 3, hits: 0, position: null },
          { engine: 'perplexity', samples: 3, hits: 0, position: null },
          { engine: 'ai_overview', samples: 3, hits: 0, position: null },
        ],
        googleOrganic: null, localPack: null, siteAnswers: false, actionId: null,
      },
      {
        id: 'q-pil-6', text: 'pilates for back pain Kuala Lumpur',
        engines: [
          { engine: 'chatgpt', samples: 3, hits: 0, position: null },
          { engine: 'gemini', samples: 3, hits: 0, position: null },
          { engine: 'claude', samples: 3, hits: 0, position: null },
          { engine: 'perplexity', samples: 3, hits: 0, position: null },
          { engine: 'ai_overview', samples: 3, hits: 0, position: null },
        ],
        googleOrganic: null, localPack: null, siteAnswers: false, actionId: 'a-pil-3',
      },
      {
        id: 'q-pil-7', text: 'pilates studio with childcare KL',
        engines: [
          { engine: 'chatgpt', samples: 3, hits: 0, position: null },
          { engine: 'gemini', samples: 3, hits: 0, position: null },
          { engine: 'claude', samples: 3, hits: 0, position: null },
          { engine: 'perplexity', samples: 3, hits: 0, position: null },
          { engine: 'ai_overview', samples: 3, hits: 0, position: null },
        ],
        googleOrganic: null, localPack: null, siteAnswers: false, actionId: null,
      },
      {
        id: 'q-pil-8', text: 'affordable pilates classes Kuala Lumpur',
        engines: [
          { engine: 'chatgpt', samples: 3, hits: 0, position: null },
          { engine: 'gemini', samples: 3, hits: 0, position: null },
          { engine: 'claude', samples: 3, hits: 0, position: null },
          { engine: 'perplexity', samples: 3, hits: 1, position: 6 },
          { engine: 'ai_overview', samples: 3, hits: 0, position: null },
        ],
        googleOrganic: null, localPack: null, siteAnswers: false, actionId: null,
      },
    ],
    rivals: [
      {
        name: 'Pavilion Pilates', domain: 'pavilionpilates.my',
        winsQuestionIds: ['q-pil-1', 'q-pil-2', 'q-pil-4', 'q-pil-6'],
        whatTheyHave: 'A separate page for reformer classes with prices and a timetable. You have none.',
        reviewCount: 210, reviewRating: 4.9, total: 61, previousTotal: null,
      },
      {
        name: 'Rebarre Studio', domain: 'rebarre.com.my',
        winsQuestionIds: ['q-pil-3', 'q-pil-6'],
        whatTheyHave: 'A page answering "is pilates safe during pregnancy" that AI quotes directly. You have none.',
        reviewCount: 156, reviewRating: 4.7, total: 52, previousTotal: null,
      },
      {
        name: 'Embodied Presence', domain: 'embodiedpresence.my',
        winsQuestionIds: ['q-pil-1'],
        whatTheyHave: 'Their address is written the same way everywhere, so AI is confident where they are.',
        reviewCount: 98, reviewRating: 4.8, total: 44, previousTotal: null,
      },
    ],
    setup: {
      site: [
        { label: 'Your homepage describes your studio', status: 'missing', detail: 'It still shows the template\'s San Francisco event text.' },
        { label: 'A page for each class you offer', status: 'missing', detail: 'No reformer, prenatal, or beginner pages exist.' },
        { label: 'Your prices and timetable are on the site', status: 'missing' },
        { label: 'Your address is written the same way everywhere', status: 'warn', detail: 'Three different versions across your website, Google and Facebook.' },
      ],
      sources: [
        { label: 'Instagram linked to your studio', status: 'ok' },
        { label: 'Listed on a local fitness directory AI reads', status: 'missing', detail: 'Rivals appear on KL fitness directories. You do not.' },
      ],
      reputation: [
        { label: 'Google reviews', value: '64', comparison: 'Pavilion Pilates has 210' },
        { label: 'Average rating', value: '4.8 stars', comparison: 'Among the best on your street' },
        { label: 'Reviews you have replied to', value: '12%', comparison: 'AI reads replies as a sign you are still open' },
      ],
      plainSummary:
        'Your reviews are genuinely strong. The problem is everything AI reads before it gets to them: your website says nothing about your studio, so AI has no reason to bring you up.',
    },
    connections: [
      {
        channel: 'website', label: 'Your website', status: 'not_connected',
        method: 'We publish new and rewritten pages through your CMS, or a one-time Oura snippet if you would rather not share a login.',
        detail: 'Not connected yet. We need this before we can fix your homepage or add class pages for you.',
      },
      {
        channel: 'listings', label: 'Directories & listings', status: 'not_connected',
        method: 'We claim and manage listings like local fitness directories on your behalf; you do a one-time ownership check.',
        detail: 'Not connected yet.',
      },
    ],
    actions: [
      {
        id: 'a-pil-1', title: 'Replace the placeholder text on your homepage', channel: 'website',
        body: 'Your homepage still shows the sample text the template came with. Until it describes your studio in Kuala Lumpur, AI has nothing of yours to repeat.',
        estLift: 6, lever: 'site', helps: ['ai', 'google'], effort: 'We do it in a day',
        owner: 'oura', draftReady: true, draftCount: 1, status: 'todo',
        steps: [
          'We rewrite your homepage so it describes your studio, your classes and your Kuala Lumpur location.',
          'You review the draft below and change anything that does not sound like you.',
          'We publish it, then re-check what AI reads about you at your next weekly check.',
        ],
        draftPreview:
          'The Pilates Collective — Reformer & mat pilates in Bangsar, Kuala Lumpur\n\nA small studio in Bangsar Baru for reformer, mat and prenatal pilates. Beginner-friendly classes seven days a week, with certified instructors and childcare on weekday mornings. Walk-ins welcome; first class 50% off.',
      },
      {
        id: 'a-pil-2', title: 'Add a page for reformer and beginner classes', channel: 'website',
        body: 'People ask AI for reformer and beginner classes near Bangsar. A page that answers those, with prices and times, gives AI something to quote.',
        estLift: 5, lever: 'site', helps: ['ai', 'google'], effort: 'We draft it, you approve',
        owner: 'oura', draftReady: true, draftCount: 2, status: 'todo',
        steps: [
          'We build two pages: one for reformer classes, one for beginners, each with prices and a timetable.',
          'You approve the two drafts below.',
          'We publish them and watch whether AI starts naming you for "reformer" and "beginner" questions.',
        ],
        draftPreview:
          'Reformer classes — from RM65\n\nSmall-group reformer sessions (max 6) for all levels, Mon–Sat. New to the reformer? Start with our Reformer Foundations block.\n\n— — —\n\nBeginner pilates in Bangsar\n\nNever done pilates? This page answers the three things everyone asks: what to wear, what to expect, and how to book your first class.',
      },
      {
        id: 'a-pil-3', title: 'Add a page answering pregnancy and back-pain questions', channel: 'website',
        body: 'Two of your rivals get named for prenatal and back-pain classes because they answer those questions on a page. You can too.',
        estLift: 4, lever: 'site', helps: ['ai'], effort: 'We draft it, you approve',
        owner: 'oura', draftReady: false, draftCount: null, status: 'todo',
      },
      {
        id: 'a-pil-4', title: 'Write your address the same way everywhere', channel: 'google_business',
        body: 'Your address appears three different ways online. Pick one and we will make your website, Google and Facebook match.',
        estLift: 3, lever: 'sources', helps: ['ai', 'google'], effort: 'We do it, you confirm the address',
        owner: 'oura', draftReady: false, draftCount: null, status: 'todo',
        steps: [
          'You tell us the one correct address.',
          'We make your website, Google and Facebook all match it exactly.',
          'We re-check that AI now reads a single, confident location for you.',
        ],
      },
      {
        id: 'a-pil-5', title: 'Reply to your recent Google reviews',
        body: 'You have replied to 12% of your reviews. Replying tells AI you are active and open, which it reads as a good sign.',
        estLift: null, lever: 'reputation', helps: ['ai'], effort: '20 minutes',
        owner: 'client', draftReady: false, draftCount: null, status: 'todo',
      },
    ],
    feed: [],
    history: [
      { month: '2026-07', total: 16, level: 1, projected: false, note: 'Your first check.' },
      { month: '2026-08', total: null, level: null, projected: true, note: 'A studio like yours that fixes its website typically reaches the high 20s.' },
      { month: '2026-09', total: null, level: null, projected: true, note: 'With class pages and a consistent address, the mid 30s is common.' },
    ],
  },
};
